// ============================================================================
// CMS Page Builder — Color Token Palette
// Centralized color options derived from Chakra theme colors.
// All CMS color selectors must use these tokens — never hardcode hex.
// ============================================================================

export interface ColorToken {
  /** Unique key stored in block props / config */
  id: string;
  /** Display label in settings UI */
  label: string;
  /** Chakra color token (passed directly to Chakra's `color` prop) */
  chakra: string;
  /** Hex value for CSS-only contexts (inline styles, TipTap marks) */
  hex: string;
  /** Optional group for visual organization in pickers */
  group?: 'brand' | 'neutral' | 'semantic';
}

/**
 * Canonical color palette for CMS blocks.
 * Covers brand colors, neutrals, and semantic colors.
 */
export const COLOR_TOKENS: ColorToken[] = [
  // Brand
  { id: 'blaze', label: 'Blaze', chakra: 'blaze.blaze', hex: '#FF5A2C', group: 'brand' },
  { id: 'blaze-light', label: 'Blaze Light', chakra: 'blaze.300', hex: '#FF9C80', group: 'brand' },
  { id: 'blaze-dark', label: 'Blaze Dark', chakra: 'blaze.700', hex: '#99361A', group: 'brand' },
  { id: 'ice', label: 'Ice', chakra: 'ice.ice', hex: '#2D84F6', group: 'brand' },
  { id: 'fresh', label: 'Fresh', chakra: 'fresh.fresh', hex: '#3CA52C', group: 'brand' },
  { id: 'electric-pink', label: 'Electric Pink', chakra: 'electric_pink.electric_pink', hex: '#FB2576', group: 'brand' },

  // Neutral
  { id: 'white', label: 'White', chakra: 'neutral.white', hex: '#FFFFFF', group: 'neutral' },
  { id: 'dune', label: 'Dune (off-white)', chakra: 'dune.dune', hex: '#FBEEE8', group: 'neutral' },
  { id: 'neutral-400', label: 'Gray', chakra: 'neutral.400', hex: '#A3A3A3', group: 'neutral' },
  { id: 'neutral-600', label: 'Dark Gray', chakra: 'neutral.600', hex: '#525252', group: 'neutral' },
  { id: 'charcoal', label: 'Charcoal', chakra: 'charcoal.charcoal', hex: '#141517', group: 'neutral' },
  { id: 'black', label: 'Black', chakra: 'neutral.black', hex: '#000000', group: 'neutral' },

  // Semantic
  { id: 'success', label: 'Success', chakra: 'success.500', hex: '#00AB65', group: 'semantic' },
  { id: 'warning', label: 'Warning', chakra: 'warning.500', hex: '#FCB529', group: 'semantic' },
  { id: 'error', label: 'Error', chakra: 'error.500', hex: '#D40000', group: 'semantic' },
  { id: 'info', label: 'Info', chakra: 'info.500', hex: '#3283BA', group: 'semantic' },
];

/** Look up a color token by ID → Chakra value. Returns undefined if not found. */
export function resolveColorToken(tokenId: string | null | undefined): string | undefined {
  if (!tokenId) return undefined;
  const token = COLOR_TOKENS.find((t) => t.id === tokenId);
  return token?.chakra;
}

/** Look up a color token by ID → hex value. Returns undefined if not found. */
export function resolveColorHex(tokenId: string | null | undefined): string | undefined {
  if (!tokenId) return undefined;
  const token = COLOR_TOKENS.find((t) => t.id === tokenId);
  return token?.hex;
}

/** Look up a hex value → token ID (for TipTap mark roundtrip). Returns undefined if no match. */
export function hexToTokenId(hex: string | null | undefined): string | undefined {
  if (!hex) return undefined;
  const normalized = hex.toUpperCase();
  const token = COLOR_TOKENS.find((t) => t.hex.toUpperCase() === normalized);
  return token?.id;
}
